/**
 * @file `renderMarkdown` — the client-side Markdown → HTML renderer for the
 *       HTML compose mode (#1606 Part 9).
 * @module @labcraft/admin/ui/emails/render-markdown
 *
 * The composer renders Markdown → HTML **once** with this, previews that
 * exact HTML, and sends it as the request body with `format: 'html'`. The
 * renderer is deliberately constrained so its output stays inside the
 * server sanitizer's allowlist (`@labcraft/email` `HtmlSanitizer`): raw HTML
 * and images are dropped, and the two GFM constructs the sanitizer would
 * otherwise strip — task-list checkboxes (`<input>`) and table-cell `align`
 * attributes — are suppressed here too. Because the emitted construct set ⊆
 * the allowlist, the authoritative server-side sanitize is a no-op on this
 * output — so "the live preview equals the sent email" holds by construction,
 * not by assertion.
 */

import { Marked } from 'marked'

/**
 * A `marked` instance constrained to the sanitizer allowlist: GitHub-flavour
 * Markdown with soft line breaks, but raw HTML + images suppressed.
 */
const md = new Marked({ gfm: true, breaks: true })
md.use({
  renderer: {
    // Raw HTML in the source is unsupported — drop the tags (any text
    // between them survives as inert plaintext, never as markup).
    html() {
      return ''
    },
    // Images are not supported in the email composer.
    image() {
      return ''
    },
    // GFM task-list checkboxes emit `<input>`, which the server sanitizer
    // strips (not in the allowlist) — drop it so the item renders as a plain
    // list item and the "preview == sent" invariant holds (F3).
    checkbox() {
      return ''
    },
    // GFM aligned tables emit an `align` attribute on cells, also outside the
    // allowlist. Re-render the cell without it so the preview matches sent.
    tablecell(token) {
      const tag = token.header ? 'th' : 'td'
      return `<${tag}>${this.parser.parseInline(token.tokens)}</${tag}>\n`
    },
  },
})

/**
 * Render Markdown source to sanitizer-safe HTML.
 *
 * @param source - The operator's Markdown.
 * @returns HTML containing only allowlisted constructs.
 */
export function renderMarkdown(source: string): string {
  return md.parse(source, { async: false })
}
